// "Varnostni dnevnik" (administrators): sign-ins, password / 2FA changes, user management.
import { useState } from 'react';
import type { AuditAction, AuditEntry } from '../../../../shared/types';
import { listAudit } from '../../lib/account';
import { formatDateTime } from '../../lib/format';
import { accountErrorText } from '../../components/AccountForms';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/Toasts';
import { LoadError, Loading, useLoaded } from './common';

export const AUDIT_PAGE_SIZE = 50;

export const AUDIT_LABEL: Record<AuditAction, string> = {
  setup_completed: 'Prva nastavitev',
  login_ok: 'Prijava',
  login_failed: 'Neuspešna prijava',
  login_locked: 'Prijava začasno omejena',
  logout: 'Odjava',
  password_changed: 'Sprememba gesla',
  totp_enabled: 'Vklop dvostopenjske prijave',
  totp_disabled: 'Izklop dvostopenjske prijave',
  recovery_code_used: 'Uporabljena obnovitvena koda',
  recovery_codes_regenerated: 'Nove obnovitvene kode',
  device_registered: 'Nova naprava Android',
  session_revoked: 'Odjava naprave',
  user_created: 'Nov uporabnik',
  user_updated: 'Sprememba uporabnika',
  user_deleted: 'Izbris uporabnika',
  settings_changed: 'Sprememba nastavitev',
  project_changed: 'Sprememba projekta',
  admin_reset: 'Ponastavitev gesla ali 2FA (skrbnik)',
  reauth_ok: 'Potrditev za skrbniško dejanje',
  reauth_failed: 'Neuspešna potrditev za skrbniško dejanje',
  login_attack_suspected: 'Sum ugibanja gesel',
  notes_auto_deleted: 'Samodejni izbris opravljenih opomnikov',
};

const WARNING_ACTIONS: ReadonlySet<AuditAction> = new Set([
  'login_failed',
  'login_locked',
  'totp_disabled',
  'recovery_code_used',
  'admin_reset',
  'reauth_failed',
  'login_attack_suspected',
]);

/** "Chrome · Android" style summary of a user agent (the full string is in the title). */
export function shortUserAgent(ua: string | null): string | null {
  if (!ua) return null;
  if (/Transkripcija-Android|okhttp/i.test(ua)) return 'aplikacija Android';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /SamsungBrowser\//.test(ua)
        ? 'Samsung Internet'
        : /Chrome\/|CriOS\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : null;
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : null;
  const parts = [browser, os].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : ua.slice(0, 60);
}

function label(action: string): string {
  return (AUDIT_LABEL as Record<string, string>)[action] ?? action;
}

function AuditItem({ entry }: { entry: AuditEntry }) {
  const ua = shortUserAgent(entry.userAgent);
  const who = entry.username ?? 'neznan uporabnik';
  return (
    <li className={`audit-entry${WARNING_ACTIONS.has(entry.action) ? ' audit-entry--warning' : ''}`}>
      <span className="audit-entry__head">
        <span className="audit-entry__action">{label(entry.action)}</span>
        <time className="audit-entry__time" dateTime={entry.at}>
          {formatDateTime(entry.at)}
        </time>
      </span>
      <span className="audit-entry__who">
        {who}
        {entry.targetUsername ? ` → ${entry.targetUsername}` : ''}
      </span>
      {entry.detail && <span className="audit-entry__detail">{entry.detail}</span>}
      {(entry.ip || ua) && (
        <span className="audit-entry__meta" title={entry.userAgent ?? undefined}>
          {[entry.ip ? `IP ${entry.ip}` : null, ua].filter(Boolean).join(' · ')}
        </span>
      )}
    </li>
  );
}

export function AuditSection() {
  const first = useLoaded(() => listAudit({ limit: AUDIT_PAGE_SIZE }), 'Varnostnega dnevnika ni bilo mogoče naložiti.');
  const [older, setOlder] = useState<AuditEntry[]>([]);
  const [olderBase, setOlderBase] = useState<AuditEntry[] | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [end, setEnd] = useState(false);

  // A reload of the first page starts over.
  const extra = olderBase === first.data ? older : [];
  const entries = [...(first.data ?? []), ...extra];
  const atEnd = olderBase === first.data ? end : (first.data?.length ?? 0) < AUDIT_PAGE_SIZE;

  const loadMore = async () => {
    const last = entries.at(-1);
    if (!last || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listAudit({ limit: AUDIT_PAGE_SIZE, before: last.id });
      setOlder([...extra, ...page]);
      setOlderBase(first.data);
      setEnd(page.length < AUDIT_PAGE_SIZE);
    } catch (err) {
      toast(accountErrorText(err, 'Starejših vnosov ni bilo mogoče naložiti.'), { kind: 'error' });
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="card" aria-labelledby="audit-title" id="dnevnik">
      <div className="card__head">
        <h2 id="audit-title" className="card__title">
          <Icon name="list" size={22} /> Varnostni dnevnik
        </h2>
        <button type="button" className="icon-btn" onClick={first.reload} aria-label="Osveži dnevnik" title="Osveži" disabled={first.loading}>
          <Icon name={first.loading ? 'spinner' : 'refresh'} size={20} />
        </button>
      </div>
      <p className="muted">Prijave, spremembe gesel in dvostopenjske prijave ter upravljanje uporabnikov. Vnosi se hranijo eno leto.</p>
      {first.error && !first.data ? (
        <LoadError message={first.error} onRetry={first.reload} />
      ) : !first.data ? (
        <Loading />
      ) : entries.length === 0 ? (
        <p className="empty">Dnevnik je prazen.</p>
      ) : (
        <>
          <ul className="audit-list">
            {entries.map((e) => (
              <AuditItem key={e.id} entry={e} />
            ))}
          </ul>
          {atEnd ? (
            <p className="muted small">To so vsi vnosi.</p>
          ) : (
            <button type="button" className="btn btn--secondary load-more" onClick={() => void loadMore()} disabled={loadingMore}>
              <Icon name={loadingMore ? 'spinner' : 'chevronDown'} size={20} /> Naloži starejše
            </button>
          )}
        </>
      )}
    </section>
  );
}
